import { useState, useEffect, useRef } from 'react';
import { checkServerHealth } from '../apiCalls';

const RETRY_INTERVAL = 10000; // retry every 10 seconds
const FAILURE_THRESHOLD = 2;  // only flip UI to "unavailable" after this many consecutive failures

export const getHealthStatusFromFailures = (failureCount, threshold = FAILURE_THRESHOLD) => {
    if (failureCount <= 0) return 'healthy';
    if (failureCount < threshold) return 'degraded';
    return 'down';
};

export const useServerHealth = () => {
    const [status, setStatus] = useState('healthy'); // optimistic: assume available until proven otherwise
    const failureCountRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        let retryTimer = null;

        const check = async () => {
            try {
                await checkServerHealth();
                if (cancelled) return;
                failureCountRef.current = 0;
                setStatus('healthy');
            } catch {
                if (cancelled) return;
                failureCountRef.current += 1;
                // Only show the maintenance page after repeated failures to avoid
                // a flicker caused by a single transient network blip on mobile.
                setStatus(getHealthStatusFromFailures(failureCountRef.current));
                retryTimer = setTimeout(check, RETRY_INTERVAL);
            }
        };

        check();
        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, []);

    return {
        isServerAvailable: status !== 'down',
        isServerDegraded: status === 'degraded',
    };
};
